import type { ReportChartId, ReportPackageKey, SolutionChartKey } from '../types';

/** Chart order of a solution package, as in the server catalogue. */
export const SOLUTION_CHART_KEYS: SolutionChartKey[] = [
  'npv_heatmap',
  'production',
  'soiling_ratio',
  'capex',
  'opex',
  'financial_comparison',
  'production_losses',
  'cleaning_schedules',
];

/** Mirror of the server catalogue: the only chart ids accepted per package, in report order. */
export const REPORT_CHARTS: Record<ReportPackageKey, ReportChartId[]> = {
  assessment: ['assessment.soiling_risk', 'assessment.soiling_ratio'],
  mitigation: SOLUTION_CHART_KEYS.map((key): ReportChartId => `mitigation.${key}`),
  operational: SOLUTION_CHART_KEYS.map((key): ReportChartId => `operational.${key}`),
  ex_post: SOLUTION_CHART_KEYS.map((key): ReportChartId => `ex_post.${key}`),
};

/** Exported charts have a fixed width; the height is the chart's own when it sets one. */
export const REPORT_CHART_WIDTH = 900;
export const REPORT_CHART_DEFAULT_HEIGHT = 450;
/** Root font size of the screen: the chart texts are sized relative to it. */
export const REPORT_CHART_FONT_SIZE = '16px';

/** Server limits on the submitted charts: a chart over a per-SVG limit is rejected. */
export const REPORT_CHART_LIMITS = {
  maxCharts: 40,
  maxSvgBytes: 2 * 1024 * 1024,
  maxSvgElements: 20_000,
  maxSvgTexts: 2_000,
  maxSvgTextCharacters: 20_000,
  /** A longer text element is rejected, not truncated. */
  maxSvgTextElementCharacters: 1_000,
  maxSvgDepth: 32,
  /** A longer `d` or `points` rejects the whole chart; inside a marker or clip path the cap is far lower. */
  maxSvgGeometryLength: 131_072,
  maxSvgResourceGeometryLength: 1_024,
  /**
   * The server drops a longer attribute or inline style, a number out of range (or written `inf`, `nan`), a transform
   * whose composed matrix (or, for a gradient, inverse matrix) is out of range, a viewBox other than four numbers or
   * too small, an arc whose radii are not both zero or both positive within the ratio, and a dash pattern of more
   * values, of other tokens than plain numbers, of a value neither zero nor long enough, or too short.
   */
  maxSvgAttributeLength: 512,
  maxSvgStyleLength: 2_048,
  maxSvgAbsNumber: 1e6,
  maxArcRadiiRatio: 1e3,
  minViewBoxSize: 1e-3,
  maxDashValues: 16,
  minDashLength: 0.5,
  minSvgDashPatternLength: 1,
  /**
   * A gradient whose vector (or radius) is shorter, in fractions of the bounding box (or percentages) or in user units,
   * loses its id: its references are dropped and only their fallback colour is drawn.
   */
  minGradientFraction: 0.01,
  minGradientUserLength: 1,
  /** Kept descendants of a resource, drawn again at each reference to it. */
  maxResourceDescendants: { marker: 8, clipPath: 8, linearGradient: 32, radialGradient: 32 } as Record<string, number>,
  /** Weighted render cost of all the charts; the server omits the charts past it. */
  maxReportRenderCost: 40_000,
  /** Kept under the 10 MiB server body limit, with room for the JSON envelope. */
  maxBodyBytes: 9 * 1024 * 1024,
};

/**
 * Render cost weights of the server: an element weighs one plus one per unit of its kept attribute characters (names
 * only for `d` and `points`), plus an opacity group (any value but `1`) of two and one per unit of descendants. A `d` or
 * `points` is parsed once per pass (drawing, each gradient painting it, each opacity group of it or an ancestor), a pass
 * weighing one per unit of its characters (doubled for a `d` with arcs, horizontal or vertical lines) times its length
 * over the linear one beyond it. A text element weighs one more, text characters one per unit, twice beyond U+02FF
 * (combining marks, right-to-left and complex scripts, CJK, emoji). A reference draws its resource descendants again.
 */
export const REPORT_CHART_COST = {
  attributeCharactersPerUnit: 64,
  opacityGroupExtra: 2,
  opacityGroupDescendantsPerUnit: 16,
  geometryLinearLength: 32_768,
  textElementExtra: 1,
  textCharactersPerUnit: 2,
  /** Last code point weighing one text character; any later one weighs `complexCharacterWeight`. */
  simpleCharacterMaxCodePoint: 0x2ff,
  complexCharacterWeight: 2,
};

/** Decimals the exported geometry is rounded to: a tenth of a pixel is invisible. */
export const REPORT_CHART_GEOMETRY_DECIMALS = 1;

/** Prefix the server gives the ids of its n-th chart, counted in their attribute characters. */
export const reportChartIdPrefix = (index: number) => `c${index}-`;
